import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { useGetEcrituresManuellesQuery, useGetCodesQuery } from "actions/comptabilite/queries";
import {
  useCreateEcritureManuelleMutation,
  useCreateEcritureMultilignesMutation,
  useDeletePieceMultilignesMutation,
  useDeleteEcritureManuelleMutation,
  useUpdateEcritureManuelleMutation,
  useWorkflowEcritureManuelleMutation,
} from "actions/comptabilite/mutations";
import ModalImporterEcritures from "./ModalImporterEcritures";
import TableLoader from "components/Loader/TableLoader";

const initialForm = {
  date: new Date().toISOString().slice(0, 10),
  libelle: "",
  code_debit: "",
  code_credit: "",
  montant: "",
};

const JOURNALS = [
  { code: "OD", label: "Opérations diverses" },
  { code: "ACHATS", label: "Journal des achats" },
  { code: "VENTES", label: "Journal des ventes" },
  { code: "BANQUE", label: "Journal de banque" },
  { code: "CAISSE", label: "Journal de caisse" },
  { code: "SALAIRES", label: "Journal des salaires" },
  { code: "FISCALITE", label: "Déclarations fiscales" },
];

const JOURNAL_LABELS = Object.fromEntries(JOURNALS.map((j) => [j.code, j.label]));

const emptyLigne = () => ({ compte: "", compte_libelle: "", debit: "", credit: "" });
const initialLignes = () => [emptyLigne(), emptyLigne()];

const inputSm = {
  padding: "6px 8px",
  fontSize: 12,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  width: "100%",
  boxSizing: "border-box",
};

const SaisieEcritures = ({ dossier_id }) => {
  const [form, setForm] = useState(initialForm);
  const [statusFilter, setStatusFilter] = useState("TOUS");
  const [createEcriture, { isLoading: creating }] = useCreateEcritureManuelleMutation();
  const [createPiece, { isLoading: creatingPiece }] = useCreateEcritureMultilignesMutation();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // ── Mode : écriture simple (paire) ou multi-comptes ──
  const [mode, setMode] = useState("simple");
  const [journal, setJournal] = useState("OD");
  const [pieceDate, setPieceDate] = useState(new Date().toISOString().slice(0, 10));
  const [pieceLibelle, setPieceLibelle] = useState("");
  const [lignes, setLignes] = useState(initialLignes);

  const { data: codesData } = useGetCodesQuery(
    { dossier_client: dossier_id },
    { skip: !dossier_id },
  );
  const comptes = useMemo(() => {
    const list = codesData?.results || codesData || [];
    return Array.isArray(list) ? list : [];
  }, [codesData]);
  const compteLabel = (code) => {
    const found = comptes.find(
      (c) => String(c.code || c.numero_compte || c.numero || "") === String(code)
    );
    return found?.libelle || found?.intitule || "";
  };

  const totaux = useMemo(() => {
    const d = lignes.reduce((s, l) => s + (Number(l.debit) || 0), 0);
    const c = lignes.reduce((s, l) => s + (Number(l.credit) || 0), 0);
    return { d, c, equilibre: d === c && d > 0 };
  }, [lignes]);

  const setLigne = (idx, field, value) => {
    setLignes((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      // Un montant saisi d'un côté vide l'autre côté
      if (field === "debit" && Number(value) > 0) next[idx].credit = "";
      if (field === "credit" && Number(value) > 0) next[idx].debit = "";
      // Mettre a jour systematiquement le libelle du compte depuis le plan des que le compte change
      if (field === "compte") {
        const lbl = compteLabel(value);
        if (lbl) next[idx].compte_libelle = lbl;
      }
      return next;
    });
  };
  const addLigne = () => setLignes((prev) => [...prev, emptyLigne()]);
  const removeLigne = (idx) =>
    setLignes((prev) => (prev.length > 2 ? prev.filter((_, i) => i !== idx) : prev));

  const resetPiece = () => {
    setPieceLibelle("");
    setLignes(initialLignes());
  };

  const handleSubmitPiece = async (e) => {
    e.preventDefault();
    if (!dossier_id) return;
    if (!pieceLibelle.trim()) {
      toast.error("Le libellé de l'écriture est obligatoire.");
      return;
    }
    const lignesValides = lignes.filter(
      (l) => l.compte && (Number(l.debit) > 0 || Number(l.credit) > 0)
    );
    if (lignesValides.length < 2) {
      toast.error("Renseignez au moins 2 lignes (compte + montant).");
      return;
    }
    if (!totaux.equilibre) {
      toast.error(
        `Écriture non équilibrée : débit ${totaux.d.toLocaleString("fr-FR")} ≠ crédit ${totaux.c.toLocaleString("fr-FR")}.`
      );
      return;
    }
    try {
      const res = await createPiece({
        dossier_id,
        date: pieceDate,
        libelle: pieceLibelle.trim(),
        journal,
        lignes: lignesValides.map((l) => ({
          compte: l.compte,
          compte_libelle: l.compte_libelle || undefined,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
        })),
      }).unwrap();
      toast.success(`Écriture ${res?.piece_ref || ""} enregistrée.`);
      resetPiece();
      refetch();
      window.dispatchEvent(new Event("compta:refresh"));
    } catch (error) {
      toast.error(error?.data?.message || "Erreur lors de l'enregistrement.");
    }
  };
  const [updateEcriture, { isLoading: updating }] = useUpdateEcritureManuelleMutation();
  const [deleteEcriture, { isLoading: deleting }] = useDeleteEcritureManuelleMutation();
  const [workflowEcriture, { isLoading: changingWorkflow }] =
    useWorkflowEcritureManuelleMutation();
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(initialForm);
  const { data, isFetching, refetch } = useGetEcrituresManuellesQuery(
    { dossier_id },
    { skip: !dossier_id },
  );

  const ecritures = data?.ecritures || [];
  const recent = useMemo(() => {
    const filtered =
      statusFilter === "TOUS"
        ? ecritures
        : ecritures.filter((item) => item.workflow_status === statusFilter);
    return filtered.slice(0, 20);
  }, [ecritures, statusFilter]);

  const onChange = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: val };
      if ((field === "code_debit" || field === "code_credit") && val) {
        const prevCode = prev[field];
        const prevLbl = prevCode ? compteLabel(prevCode) : "";
        const newLbl = compteLabel(val);
        if (newLbl && (!prev.libelle || prev.libelle === prevLbl)) {
          next.libelle = newLbl;
        }
      }
      return next;
    });
  };
  const onChangeEdit = (field) => (e) => {
    const val = e.target.value;
    setEditForm((prev) => {
      const next = { ...prev, [field]: val };
      if ((field === "code_debit" || field === "code_credit") && val) {
        const prevCode = prev[field];
        const prevLbl = prevCode ? compteLabel(prevCode) : "";
        const newLbl = compteLabel(val);
        if (newLbl && (!prev.libelle || prev.libelle === prevLbl)) {
          next.libelle = newLbl;
        }
      }
      return next;
    });
  };

  useEffect(() => {
    const handleRefresh = () => {
      refetch();
    };
    window.addEventListener("compta:refresh", handleRefresh);
    return () => window.removeEventListener("compta:refresh", handleRefresh);
  }, [refetch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dossier_id) return;

    if (!form.libelle || !form.code_debit || !form.code_credit || !form.montant) {
      toast.error("Veuillez renseigner tous les champs obligatoires.");
      return;
    }

    if (form.code_debit === form.code_credit) {
      toast.error("Débit et crédit doivent être différents.");
      return;
    }

    try {
      await createEcriture({
        dossier_id,
        date: form.date,
        libelle: form.libelle,
        code_debit: Number(form.code_debit),
        code_credit: Number(form.code_credit),
        montant: Number(form.montant),
        journal,
      }).unwrap();

      toast.success("Écriture manuelle enregistrée.");
      setForm((prev) => ({ ...initialForm, date: prev.date }));
      refetch();
      window.dispatchEvent(new Event("compta:refresh"));
    } catch (error) {
      toast.error(
        error?.data?.message || "Erreur lors de l'enregistrement de l'écriture.",
      );
    }
  };

  const handleWorkflow = async (ecriture, action) => {
    try {
      await workflowEcriture({ ecriture_id: ecriture.id, action }).unwrap();
      toast.success("Statut mis à jour.");
      refetch();
      window.dispatchEvent(new Event("compta:refresh"));
    } catch (error) {
      toast.error(error?.data?.message || "Transition de workflow refusée.");
    }
  };

  const startEdit = (item) => {
    if (item.piece_ref) {
      toast.info(
        "Cette écriture fait partie d'une pièce multi-comptes (" + item.piece_ref +
        ") : la modification ligne par ligne est désactivée pour préserver l'équilibre."
      );
      return;
    }
    setEditingId(item.id);
    setEditForm({
      date: item?.date ? new Date(item.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      libelle: item.libelle_original || item.libelle || "",
      code_debit: item.code_debit || "",
      code_credit: item.code_credit || "",
      montant: item.debit || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(initialForm);
  };

  const submitEdit = async (item) => {
    try {
      await updateEcriture({
        ecriture_id: item.id,
        date: editForm.date,
        libelle: editForm.libelle,
        code_debit: Number(editForm.code_debit),
        code_credit: Number(editForm.code_credit),
        montant: Number(editForm.montant),
      }).unwrap();
      toast.success("Écriture modifiée.");
      setEditingId(null);
      refetch();
      window.dispatchEvent(new Event("compta:refresh"));
    } catch (error) {
      toast.error(error?.data?.message || "Modification impossible.");
    }
  };

  const [deletePiece] = useDeletePieceMultilignesMutation();

  const handleDelete = async (item) => {
    if (item.piece_ref) {
      const confirmed = window.confirm(
        `Supprimer toute la pièce ${item.piece_ref} (toutes ses lignes) ?`
      );
      if (!confirmed) return;
      try {
        await deletePiece({ dossier_id, piece_ref: item.piece_ref }).unwrap();
        toast.success("Pièce supprimée.");
        refetch();
        window.dispatchEvent(new Event("compta:refresh"));
      } catch (error) {
        toast.error(error?.data?.message || "Suppression impossible.");
      }
      return;
    }
    const confirmed = window.confirm("Supprimer cette écriture ?");
    if (!confirmed) return;
    try {
      await deleteEcriture({ ecriture_id: item.id }).unwrap();
      toast.success("Écriture supprimée.");
      refetch();
      window.dispatchEvent(new Event("compta:refresh"));
    } catch (error) {
      toast.error(error?.data?.message || "Suppression impossible.");
    }
  };

  const btnStyle = (variant = "default") => ({
    border: "none",
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    background:
      variant === "primary" ? "#008fa0"
      : variant === "success" ? "#16a34a"
      : variant === "danger" ? "#dc2626"
      : variant === "warn" ? "#d97706"
      : "#e2e8f0",
    color: variant === "default" ? "#334155" : "#fff",
  });

  const statusBadge = (status) => {
    const map = {
      BROUILLON: { bg: "#e2e8f0", fg: "#334155", label: "Brouillon" },
      A_VALIDER: { bg: "#fef3c7", fg: "#92400e", label: "À valider" },
      VALIDE: { bg: "#dcfce7", fg: "#166534", label: "Validée" },
    };
    const token = map[status] || map.BROUILLON;
    return (
      <span
        style={{
          background: token.bg,
          color: token.fg,
          borderRadius: 20,
          padding: "2px 8px",
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        {token.label}
      </span>
    );
  };

  return (
    <div style={{ background: "#f8fafc", border: "1px solid #dbe5e8", borderRadius: 10, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ marginTop: 0, marginBottom: 12, color: "#0f172a" }}>
          Saisie manuelle des écritures comptables
        </h3>
        <div style={{ display: "inline-flex", gap: 6, marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => setMode("simple")}
            style={btnStyle(mode === "simple" ? "primary" : "default")}
          >
            Écriture simple
          </button>
          <button
            type="button"
            onClick={() => setMode("multi")}
            style={btnStyle(mode === "multi" ? "primary" : "default")}
            title="Débiter et créditer plusieurs comptes dans la même pièce (salaires, fiscalité...)"
          >
            Écriture multi-comptes
          </button>
          <button
            type="button"
            onClick={() => setIsImportModalOpen(true)}
            disabled={!dossier_id}
            style={{
              ...btnStyle("default"),
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            title="Importer un fichier Excel ou CSV d'écritures (format Sage ou SYSCOHADA, tous comptes acceptés)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Importer Excel / CSV
          </button>
        </div>
      </div>

      {/* Modal d'importation des écritures avec téléchargement de modèle */}
      <ModalImporterEcritures
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        dossier_id={dossier_id}
        onSuccess={() => {
          refetch();
          window.dispatchEvent(new Event("compta:refresh"));
        }}
      />

      {mode === "multi" && (
        <form onSubmit={handleSubmitPiece} style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ minWidth: 190 }}>
              <label style={{ fontSize: 11, color: "#64748b" }}>Journal</label>
              <select
                value={journal}
                onChange={(e) => setJournal(e.target.value)}
                style={{ ...inputSm, padding: "8px" }}
              >
                {JOURNALS.map((j) => (
                  <option key={j.code} value={j.code}>{j.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#64748b" }}>Date</label>
              <input
                type="date"
                value={pieceDate}
                onChange={(e) => setPieceDate(e.target.value)}
                style={inputSm}
              />
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <label style={{ fontSize: 11, color: "#64748b" }}>Libellé de la pièce</label>
              <input
                type="text"
                placeholder="Ex : Paie du personnel - septembre"
                value={pieceLibelle}
                onChange={(e) => setPieceLibelle(e.target.value)}
                style={inputSm}
              />
            </div>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#e2e8f0" }}>
                <th style={{ padding: 6, textAlign: "left" }}>Compte</th>
                <th style={{ padding: 6, textAlign: "left" }}>Intitulé du compte</th>
                <th style={{ padding: 6, textAlign: "right", width: 140 }}>Débit</th>
                <th style={{ padding: 6, textAlign: "right", width: 140 }}>Crédit</th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l, idx) => (
                <tr key={idx}>
                  <td style={{ padding: 4 }}>
                    <input
                      type="text"
                      placeholder="Ex : 661100"
                      value={l.compte}
                      onChange={(e) => setLigne(idx, "compte", e.target.value)}
                      list="comptes-plan"
                      style={inputSm}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <input
                      type="text"
                      placeholder="Intitulé (optionnel)"
                      value={l.compte_libelle}
                      onChange={(e) => setLigne(idx, "compte_libelle", e.target.value)}
                      style={inputSm}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={l.debit}
                      onChange={(e) => setLigne(idx, "debit", e.target.value)}
                      style={{ ...inputSm, textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={l.credit}
                      onChange={(e) => setLigne(idx, "credit", e.target.value)}
                      style={{ ...inputSm, textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: 4, textAlign: "center" }}>
                    <button
                      type="button"
                      onClick={() => removeLigne(idx)}
                      disabled={lignes.length <= 2}
                      style={{ ...btnStyle("danger"), padding: "2px 8px" }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
              <tr style={{ background: "#f1f5f9", fontWeight: 700 }}>
                <td colSpan={2} style={{ padding: 6, textAlign: "right" }}>
                  TOTAUX
                  <span
                    style={{
                      marginLeft: 10,
                      fontSize: 11,
                      fontWeight: 600,
                      color: totaux.equilibre ? "#16a34a" : "#dc2626",
                    }}
                  >
                    {totaux.equilibre ? "✓ Équilibrée" : "✗ Non équilibrée"}
                  </span>
                </td>
                <td style={{ padding: 6, textAlign: "right" }}>
                  {totaux.d.toLocaleString("fr-FR")}
                </td>
                <td style={{ padding: 6, textAlign: "right" }}>
                  {totaux.c.toLocaleString("fr-FR")}
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <datalist id="comptes-plan">
            {comptes.map((c) => (
              <option
                key={c.id || c.code || c.numero_compte}
                value={c.code || c.numero_compte || c.numero}
              >
                {c.libelle || c.intitule}
              </option>
            ))}
          </datalist>

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="button" onClick={addLigne} style={btnStyle("default")}>
              + Ajouter une ligne
            </button>
            <button
              type="submit"
              disabled={creatingPiece || !totaux.equilibre}
              style={{
                ...btnStyle("primary"),
                padding: "8px 16px",
                opacity: totaux.equilibre ? 1 : 0.6,
              }}
            >
              {creatingPiece ? "Enregistrement..." : "Enregistrer la pièce"}
            </button>
          </div>
        </form>
      )}

      {mode === "simple" && (
      <form
        onSubmit={handleSubmit}
        style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(180px, 1fr))", gap: 10 }}
      >
        <select
          value={journal}
          onChange={(e) => setJournal(e.target.value)}
          title="Journal comptable"
        >
          {JOURNALS.map((j) => (
            <option key={j.code} value={j.code}>{j.label}</option>
          ))}
        </select>
        <input type="date" value={form.date} onChange={onChange("date")} />
        <input
          type="number"
          placeholder="Compte débit (ex: 658)"
          value={form.code_debit}
          onChange={onChange("code_debit")}
        />
        <input
          type="number"
          placeholder="Compte crédit (ex: 471)"
          value={form.code_credit}
          onChange={onChange("code_credit")}
        />
        <input
          style={{ gridColumn: "span 2" }}
          type="text"
          placeholder="Libellé comptable"
          value={form.libelle}
          onChange={onChange("libelle")}
        />
        <input
          type="number"
          placeholder="Montant"
          value={form.montant}
          onChange={onChange("montant")}
        />
        <button
          type="submit"
          disabled={creating}
          style={{
            gridColumn: "span 3",
            background: "#008fa0",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "10px 12px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {creating ? "Enregistrement..." : "Enregistrer l'écriture"}
        </button>
      </form>
      )}

      <div style={{ marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h4 style={{ margin: "0 0 8px 0", color: "#334155" }}>Dernières écritures</h4>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="TOUS">Tous</option>
            <option value="BROUILLON">Brouillon</option>
            <option value="A_VALIDER">À valider</option>
            <option value="VALIDE">Validée</option>
          </select>
        </div>
        {isFetching ? (
          <TableLoader
            message="Chargement des dernières écritures..."
            subMessage="Synchronisation du journal des saisies manuelles"
            minHeight="180px"
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#e2e8f0" }}>
                  <th style={{ padding: 8, textAlign: "left" }}>Date</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Libellé</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Pièce / Journal</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Statut</th>
                  <th style={{ padding: 8, textAlign: "right" }}>Débit</th>
                  <th style={{ padding: 8, textAlign: "right" }}>Crédit</th>
                  <th style={{ padding: 8, textAlign: "right" }}>Montant</th>
                  <th style={{ padding: 8, textAlign: "center" }}>Workflow</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: 10, textAlign: "center", color: "#94a3b8" }}>
                      Aucune écriture trouvée
                    </td>
                  </tr>
                ) : (
                  recent.map((item) => (
                    <tr key={item.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: 8 }}>
                        {editingId === item.id ? (
                          <input type="date" value={editForm.date} onChange={onChangeEdit("date")} />
                        ) : (
                          item.date ? new Date(item.date).toLocaleDateString("fr-FR") : ''
                        )}
                      </td>
                      <td style={{ padding: 8 }}>
                        {editingId === item.id ? (
                          <input
                            type="text"
                            value={editForm.libelle}
                            onChange={onChangeEdit("libelle")}
                            style={{ minWidth: 220 }}
                          />
                        ) : (
                          item.libelle_original || item.libelle
                        )}
                      </td>
                      <td style={{ padding: 8, fontSize: 11, color: "#64748b" }}>
                        {item.piece_ref ? (
                          <>
                            <strong>{item.piece_ref}</strong>
                            <br />
                            {JOURNAL_LABELS[item.journal_type] || item.journal_type}
                          </>
                        ) : (
                          JOURNAL_LABELS[item.journal_type] || ""
                        )}
                      </td>
                      <td style={{ padding: 8 }}>{statusBadge(item.workflow_status)}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>
                        {editingId === item.id ? (
                          <input
                            type="number"
                            value={editForm.code_debit}
                            onChange={onChangeEdit("code_debit")}
                            style={{ width: 96 }}
                          />
                        ) : (
                          item.code_debit
                        )}
                      </td>
                      <td style={{ padding: 8, textAlign: "right" }}>
                        {editingId === item.id ? (
                          <input
                            type="number"
                            value={editForm.code_credit}
                            onChange={onChangeEdit("code_credit")}
                            style={{ width: 96 }}
                          />
                        ) : (
                          item.code_credit
                        )}
                      </td>
                      <td style={{ padding: 8, textAlign: "right" }}>
                        {editingId === item.id ? (
                          <input
                            type="number"
                            value={editForm.montant}
                            onChange={onChangeEdit("montant")}
                            style={{ width: 120 }}
                          />
                        ) : (
                          `${Number(item.debit || item.credit || 0).toLocaleString("fr-FR")} FCFA`
                        )}
                      </td>
                      <td style={{ padding: 8, textAlign: "center" }}>
                        <div style={{ display: "inline-flex", gap: 6 }}>
                          {item.workflow_editable && item.workflow_status === "BROUILLON" && (
                            <button
                              type="button"
                              style={btnStyle("primary")}
                              disabled={changingWorkflow}
                              onClick={() => handleWorkflow(item, "soumettre")}
                            >
                              Soumettre
                            </button>
                          )}
                          {item.workflow_editable && item.workflow_status === "A_VALIDER" && (
                            <>
                              <button
                                type="button"
                                style={btnStyle("success")}
                                disabled={changingWorkflow}
                                onClick={() => handleWorkflow(item, "valider")}
                              >
                                Valider
                              </button>
                              <button
                                type="button"
                                style={btnStyle("warn")}
                                disabled={changingWorkflow}
                                onClick={() => handleWorkflow(item, "reouvrir")}
                              >
                                Reouvrir
                              </button>
                            </>
                          )}
                          {item.workflow_editable && item.workflow_status === "VALIDE" && (
                            <button
                              type="button"
                              style={btnStyle("warn")}
                              disabled={changingWorkflow}
                              onClick={() => handleWorkflow(item, "reouvrir")}
                            >
                              Reouvrir
                            </button>
                          )}
                          {!item.workflow_editable && editingId !== item.id && (
                            <>
                              <span style={{ color: "#64748b", fontSize: 11 }}>
                                Auto (vente/achat)
                              </span>
                              <button type="button" style={btnStyle("default")} onClick={() => startEdit(item)}>
                                Corriger
                              </button>
                            </>
                          )}
                          {!item.workflow_editable && editingId === item.id && (
                            <>
                              <button type="button" style={btnStyle("success")} disabled={updating} onClick={() => submitEdit(item)}>
                                Enregistrer
                              </button>
                              <button type="button" style={btnStyle("default")} onClick={cancelEdit}>
                                Annuler
                              </button>
                            </>
                          )}
                          {item.workflow_editable && editingId !== item.id && (
                            <>
                              <button type="button" style={btnStyle("default")} onClick={() => startEdit(item)}>
                                Modifier
                              </button>
                              <button type="button" style={btnStyle("danger")} disabled={deleting} onClick={() => handleDelete(item)}>
                                Supprimer
                              </button>
                            </>
                          )}
                          {item.workflow_editable && editingId === item.id && (
                            <>
                              <button type="button" style={btnStyle("success")} disabled={updating} onClick={() => submitEdit(item)}>
                                Enregistrer
                              </button>
                              <button type="button" style={btnStyle("default")} onClick={cancelEdit}>
                                Annuler
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaisieEcritures;
